/* eslint-disable @typescript-eslint/comma-dangle */
import { Size, TargetElement } from './types';

enum Direction {
  TOP = 'top',
  BOTTOM = 'bottom',
  RIGHT = 'right',
  LEFT = 'left',
}

enum Align {
  START = 'left',
  END = 'right',
  CENTER = 'center',
}

interface GetPositionProps {
  tooltip: HTMLDivElement;
  target: TargetElement;
  direction: string;
  alignMode: string;
  hasArrow?: boolean;
  size: Size;
  shift?: number;
}

interface PositionProps {
  hasArrow?: boolean;
  size: Size;
  shift?: number;
}

interface CursorOutsideTooltipProps {
  event: MouseEvent;
  size: Size;
  target: TargetElement | null;
  position: string;
}

const SPACING = 4;
const LARGE_ARROW_SIZE = 16;
const MEDIUM_ARROW_SIZE = 16;
const SMALL_ARROW_SIZE = 8;

const LARGE_ARROW_HEIGHT = 7;
const MEDIUM_ARROW_HEIGHT = 5;

const ARROW_SIZE_MAP: { [key: string]: number } = {
  large: LARGE_ARROW_SIZE,
  medium: MEDIUM_ARROW_SIZE,
  small: SMALL_ARROW_SIZE,
};

const SHIFT_MAP: { [key: string]: number } = {
  large: LARGE_ARROW_HEIGHT,
  medium: MEDIUM_ARROW_HEIGHT,
  small: 0,
};

function getArrowSize(size: Size) {
  return ARROW_SIZE_MAP[size] || SMALL_ARROW_SIZE;
}

function getShift(size: Size) {
  return SHIFT_MAP[size] + SPACING;
}

function checkLeftRightWidthSufficient(
  tooltip: HTMLDivElement,
  target: TargetElement,
  size: Size
) {
  const arrowSize = getArrowSize(size);
  const targetRect = target.getBoundingClientRect();
  const clientWidth = document.documentElement.clientWidth;
  const deadSpace = Math.min(targetRect.left, clientWidth - targetRect.right);
  const totalWidth =
    tooltip.offsetWidth + target.offsetWidth + arrowSize + deadSpace;

  return totalWidth < clientWidth;
}

export function getScrollTop() {
  return window.pageYOffset || document.body.scrollTop || 0;
}

export function getScrollLeft() {
  return window.pageXOffset || document.body.scrollLeft || 0;
}

function checkTargetSufficientlyVisible(
  target: TargetElement,
  tooltip: HTMLDivElement,
  size: Size
) {
  const arrowSize = getArrowSize(size);
  const targetRect = target.getBoundingClientRect();
  const bottomOverhang = targetRect.bottom > window.innerHeight;
  const topOverhang = targetRect.top < 0;

  if (topOverhang && bottomOverhang) {
    return true;
  }

  if (target.offsetHeight > tooltip.offsetHeight) {
    const halfTargetHeight = target.offsetHeight / 2;
    const bottomOverhangAmount = targetRect.bottom - window.innerHeight;
    const topOverhangAmount = -targetRect.top;

    const targetCenterToBottomOfWindow =
      halfTargetHeight - bottomOverhangAmount;
    const targetCenterToTopOfWindow = halfTargetHeight - topOverhangAmount;

    return (
      targetCenterToBottomOfWindow >= arrowSize &&
      targetCenterToTopOfWindow >= arrowSize
    );
  }

  return !bottomOverhang && !topOverhang;
}

function getDirection(
  currentDirection: string,
  tooltip: HTMLDivElement,
  target: TargetElement,
  size: Size,
  recursive: boolean = false
): string {
  const arrowSize = getArrowSize(size);

  // can't switch until target is rendered
  if (!target) {
    return currentDirection;
  }

  const targetRect = target.getBoundingClientRect();
  const heightOfTipWithArrow = tooltip.offsetHeight + arrowSize;

  const spaceBelowTarget = window.innerHeight - targetRect.bottom;
  const spaceAboveTarget = targetRect.top;

  const hasSpaceBelow = spaceBelowTarget >= heightOfTipWithArrow;
  const hasSpaceAbove = spaceAboveTarget >= heightOfTipWithArrow;

  const documentWidth = document.documentElement.clientWidth;

  switch (currentDirection) {
    case Direction.RIGHT:
      // if the window is not wide enough fallback to top (fallback to down)
      if (
        !checkLeftRightWidthSufficient(tooltip, target, size) ||
        !checkTargetSufficientlyVisible(target, tooltip, size)
      ) {
        return getDirection(Direction.TOP, tooltip, target, size, true);
      }

      if (documentWidth - targetRect.right < tooltip.offsetWidth + arrowSize) {
        return Direction.LEFT;
      }

      return Direction.RIGHT;
    case Direction.LEFT:
      // if the window is not wide enough try top (fallback to down)
      if (
        !checkLeftRightWidthSufficient(tooltip, target, size) ||
        !checkTargetSufficientlyVisible(target, tooltip, size)
      ) {
        return getDirection(Direction.TOP, tooltip, target, size, true);
      }

      if (targetRect.left < tooltip.offsetWidth + arrowSize) {
        return Direction.RIGHT;
      }

      return Direction.LEFT;
    case Direction.TOP:
      if (!hasSpaceAbove) {
        if (hasSpaceBelow) {
          return Direction.BOTTOM;
        }

        if (!recursive) {
          return getDirection(Direction.RIGHT, tooltip, target, size, true);
        }
      }

      return Direction.TOP;
    case Direction.BOTTOM:
    default:
      if (!hasSpaceBelow) {
        if (hasSpaceAbove) {
          return Direction.TOP;
        }

        if (!recursive) {
          return getDirection(Direction.RIGHT, tooltip, target, size, true);
        }
      }

      return Direction.BOTTOM;
  }
}

export function parseAlign(direction: string): string {
  const directionArray = direction.split('-');

  if (directionArray.length > 1) {
    return directionArray[1];
  }

  // left and right always on center
  return 'center';
}

function getTopBottomPosition({
  tooltip,
  target,
  direction,
  alignMode,
  hasArrow = true,
  size,
  shift = getShift(size),
}: GetPositionProps) {
  const arrowSize = getArrowSize(size);
  let left = -10000000;
  let top;

  if (tooltip) {
    const scrollLeft = getScrollLeft();

    const targetRect = target.getBoundingClientRect();
    const targetLeft = targetRect.left + scrollLeft;

    const halfTargetWidth = Math.round(target.offsetWidth / 2);
    const tipWidth = tooltip.offsetWidth;
    const arrowCenter = targetLeft + halfTargetWidth;
    const arrowLeft = arrowCenter - arrowSize * 2;
    const arrowRight = arrowCenter + arrowSize * 2;

    if (alignMode === Align.START) {
      left = hasArrow ? Math.min(arrowLeft, targetLeft) : targetLeft;
    } else if (alignMode === Align.END) {
      const rightWithArrow = Math.max(
        arrowRight,
        targetLeft + target.offsetWidth
      );
      const rightEdge = hasArrow
        ? rightWithArrow
        : targetLeft + target.offsetWidth;

      left = Math.max(rightEdge - tipWidth, scrollLeft);
    } else {
      const centeredLeft =
        targetLeft + halfTargetWidth - Math.round(tipWidth / 2);

      left = Math.max(centeredLeft, scrollLeft);
    }

    const rightOfTip = left + tipWidth;
    const rightOfScreen = scrollLeft + document.documentElement.clientWidth;
    const rightOverhang = rightOfTip - rightOfScreen;

    // if it's not enough space form the right place tooltip on center
    if (left === 0 || rightOverhang > 0) {
      const centeredLeft =
        targetLeft + halfTargetWidth - Math.round(tipWidth / 2);

      left = Math.max(centeredLeft, scrollLeft);
    }

    if (direction === Direction.TOP) {
      top = targetRect.top + getScrollTop() - tooltip.offsetHeight - shift;
    } else {
      top = targetRect.bottom + getScrollTop() + shift;
    }
  }

  return {
    left,
    top,
  };
}

function getLeftRightPosition({
  tooltip,
  target,
  direction,
  alignMode,
  hasArrow = true,
  size,
  shift = getShift(size),
}: GetPositionProps) {
  const arrowSize = getArrowSize(size);
  let left = -10000000;
  let top = 0;

  if (tooltip) {
    const scrollTop = getScrollTop();
    const scrollLeft = getScrollLeft();
    const targetRect = target.getBoundingClientRect();
    const targetTop = targetRect.top + scrollTop;
    const halfTargetHeight = Math.round(target.offsetHeight / 2);
    const arrowTop = targetTop + halfTargetHeight - arrowSize;
    const arrowBottom =
      targetRect.top + scrollTop + halfTargetHeight + arrowSize;

    if (alignMode === Align.START) {
      top = hasArrow ? Math.min(targetTop, arrowTop) : targetTop;
    } else if (alignMode === Align.END) {
      const topForBottomAlign =
        targetRect.bottom + scrollTop - tooltip.offsetHeight;

      top = hasArrow
        ? Math.max(topForBottomAlign, arrowBottom - tooltip.offsetHeight)
        : topForBottomAlign;
    } else {
      const centeredTop = Math.max(
        targetTop + halfTargetHeight - Math.round(tooltip.offsetHeight / 2),
        scrollTop
      );

      top = Math.min(centeredTop, arrowTop);
    }

    const bottomOverhang =
      top - scrollTop + tooltip.offsetHeight - window.innerHeight;

    if (bottomOverhang > 0) {
      top = Math.max(top - bottomOverhang, arrowBottom - tooltip.offsetHeight);
    }

    if (direction === Direction.RIGHT) {
      left = targetRect.right + scrollLeft + shift;
    } else {
      left = targetRect.left - tooltip.offsetWidth + scrollLeft - shift;
    }
  }

  return {
    left,
    top,
  };
}

export default function positions(
  tooltip: HTMLDivElement,
  target: TargetElement,
  direction: string,
  props: PositionProps
) {
  const alignMode = parseAlign(direction);
  const placement = direction.split('-')[0];

  const realDirection = getDirection(placement, tooltip, target, props.size);
  const isTopOrBottom =
    realDirection === Direction.TOP || realDirection === Direction.BOTTOM;

  const tipPosition = isTopOrBottom
    ? getTopBottomPosition({
        tooltip,
        target,
        alignMode,
        direction: realDirection,
        ...props,
      })
    : getLeftRightPosition({
        tooltip,
        target,
        alignMode: isTopOrBottom ? alignMode : 'center',
        direction: realDirection,
        ...props,
      });

  return {
    styles: {
      ...tipPosition,
      opacity: 1,
    },
    placement: isTopOrBottom ? `${realDirection}-${alignMode}` : realDirection,
  };
}

export const eventNameMatcher = {
  mouseenter: 'onMouseEnter',
  focus: 'onFocus',
  click: 'onClick',
};

export function isCursorOutsideTooltipBoundaries({
  event,
  target,
  size,
  position,
}: CursorOutsideTooltipProps) {
  const targetRect = target && target.getBoundingClientRect();

  if (!targetRect) {
    return true;
  }

  const { clientX, clientY } = event;
  const offset = getShift(size);
  const placement = position.split('-')[0];

  const topDistance = placement === 'top' ? offset : 0;
  const bottomDistance = placement === 'bottom' ? offset : 0;
  const leftDistance = placement === 'left' ? offset : 0;
  const rightDistance = placement === 'right' ? offset : 0;

  const exceedsTop = targetRect.top - clientY > topDistance;
  const exceedsBottom = clientY - targetRect.bottom > bottomDistance;
  const exceedsLeft = targetRect.left - clientX > leftDistance;
  const exceedsRight = clientX - targetRect.right > rightDistance;

  return exceedsTop || exceedsBottom || exceedsLeft || exceedsRight;
}
